import React, { useEffect, useMemo } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { TaxStatCard, ComplianceActivityFeed, ComplianceAlerts, ExportActions } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useTaxComplianceStore } from '../../../store/taxComplianceStore';
import { TaxStat, ActivityItem, ComplianceAlert } from './taxComplianceTypes';
import styles from './AdminTaxCompliance.module.css';

const JurisdictionIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 6h-2.18c.07-.44.18-.86.18-1.3C18 2.57 15.43 0 12.3 0c-1.63 0-3.08.79-4.04 2L12 6.4 15.74 2c.44.57.96 1.08 1.56 1.5L13.06 8H12L7.5 2.5C6.54 1.2 5.03.4 3.3.4 1.48.4 0 1.88 0 3.7c0 .44.11.86.18 1.3H0v14c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 14H4V8h16v12z" />
  </svg>
);
const RestrictedIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.68L5.68 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.68L18.32 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" />
  </svg>
);
const ConflictIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  </svg>
);
const BlockedIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
  </svg>
);
const StatesIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
  </svg>
);

const statIcons = [JurisdictionIcon, RestrictedIcon, ConflictIcon, BlockedIcon, StatesIcon];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${Math.max(m, 1)} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const dotByKind: Record<string, string> = {
  rule_update: '#2563EB',
  product_restricted: '#DC2626',
  exemption: '#16A34A',
  order_blocked: '#DC2626',
  other: '#6B7280',
};

const AdminTaxCompliance: React.FC = () => {
  const overview = useTaxComplianceStore((s) => s.overview);
  const activityRaw = useTaxComplianceStore((s) => s.activity);
  const alertsRaw = useTaxComplianceStore((s) => s.alerts);
  const loading = useTaxComplianceStore((s) => s.loading);
  const error = useTaxComplianceStore((s) => s.error);
  const fetchAll = useTaxComplianceStore((s) => s.fetchAll);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const taxStats: TaxStat[] = useMemo(() => {
    if (!overview) return [];
    return [
      {
        label: 'Active Tax Jurisdictions',
        subtext: `Across ${overview.states_configured} states`,
        value: String(overview.active_jurisdictions).padStart(2, '0'),
        valueColor: '#7C3AED',
        iconBg: '#EDE9FE',
        iconColor: '#7C3AED',
      },
      {
        label: 'Restricted Products',
        subtext: 'Age/State restrictions',
        value: String(overview.restricted_products).padStart(2, '0'),
        valueColor: '#EA580C',
        iconBg: '#FFF7ED',
        iconColor: '#EA580C',
      },
      {
        label: 'Tax Rule Conflicts',
        subtext: 'Action required',
        value: String(overview.tax_rule_conflicts).padStart(2, '0'),
        valueColor: '#D97706',
        iconBg: '#FFFBEB',
        iconColor: '#D97706',
      },
      {
        label: 'Blocked Orders Today',
        subtext: 'Requires resolution',
        value: String(overview.blocked_orders_today).padStart(2, '0'),
        valueColor: '#DC2626',
        iconBg: '#FEF2F2',
        iconColor: '#DC2626',
        badge: overview.blocked_orders_today > 0
          ? { text: 'URGENT', color: '#DC2626', bg: '#FEF2F2' }
          : undefined,
      },
      {
        label: 'States Configured',
        subtext: '✓ All active markets',
        value: `${overview.states_configured}/${overview.states_total}`,
        valueColor: '#16A34A',
        iconBg: '#ECFDF5',
        iconColor: '#16A34A',
        badge: { text: 'THIS WEEK', color: '#6B7280', bg: '#F3F4F6' },
      },
    ];
  }, [overview]);

  const activityFeed: ActivityItem[] = useMemo(() => activityRaw.map((a) => ({
    title: a.title,
    description: a.description,
    timestamp: relativeTime(a.occurred_at),
    source: a.source,
    dotColor: dotByKind[a.kind] || dotByKind.other,
  })), [activityRaw]);

  const complianceAlerts: ComplianceAlert[] = useMemo(() => alertsRaw.map((a) => {
    if (a.level === 'warning') {
      return { title: a.title, subtext: a.subtext, bg: '#FFFBEB', borderColor: '#FDE68A', titleColor: '#92400E' };
    }
    if (a.level === 'critical') {
      return { title: a.title, subtext: a.subtext, bg: '#FEF2F2', borderColor: '#FECACA', titleColor: '#991B1B' };
    }
    return { title: a.title, subtext: a.subtext, bg: '#F0FDF4', borderColor: '#BBF7D0', titleColor: '#15803D' };
  }), [alertsRaw]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !overview}
        error={error}
        empty={false}
        onRetry={() => fetchAll()}
      >
        <div className={styles.statsGrid}>
          {taxStats.map((stat, i) => (
            <TaxStatCard key={stat.label} stat={stat} icon={statIcons[i]} />
          ))}
        </div>

        <div className={styles.mainGrid}>
          <ComplianceActivityFeed items={activityFeed} />

          <div className={styles.rightPanel}>
            <div className={styles.rightCard}>
              <ComplianceAlerts alerts={complianceAlerts} />
              <ExportActions overview={overview} activity={activityRaw} />
            </div>
          </div>
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const AdminTaxCompliancePage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminTaxCompliance />
  </ProtectedRoute>
);

export { AdminTaxCompliance };
export default AdminTaxCompliance;
